import React from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Building2, Briefcase, Calendar, Star, Eye } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

/**
 * Compute the actual publication date as DD/MM/YYYY.
 * Handles three cases:
 *  - ISO date string (e.g. "2026-05-08T...") → format directly
 *  - Relative French text (e.g. "il y a 3 jours", "il y a 5 heures") → derive from loaded_at
 *  - Fallback: use loaded_at, else "—"
 */
const formatPublicationDate = (job) => {
  const fmt = (d) => {
    if (!d || isNaN(d.getTime())) return null;
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const raw = job.date_publication;
  // Case 1: ISO date
  if (raw && raw.includes('T')) {
    const d = new Date(raw);
    const out = fmt(d);
    if (out) return out;
  }

  // Case 2: relative French text → compute offset from loaded_at (or now)
  const baseDate = job.loaded_at ? new Date(job.loaded_at) : new Date();
  if (raw && typeof raw === 'string') {
    const lower = raw.toLowerCase();
    const m = lower.match(/il y a\s+(\d+)\s*(heure|jour|semaine|mois|minute|min)/);
    if (m) {
      const n = parseInt(m[1], 10);
      const unit = m[2];
      const result = new Date(baseDate);
      if (unit.startsWith('heure')) result.setHours(result.getHours() - n);
      else if (unit.startsWith('jour')) result.setDate(result.getDate() - n);
      else if (unit.startsWith('semaine')) result.setDate(result.getDate() - n * 7);
      else if (unit.startsWith('mois')) result.setMonth(result.getMonth() - n);
      else if (unit.startsWith('minute') || unit === 'min') result.setMinutes(result.getMinutes() - n);
      const out = fmt(result);
      if (out) return out;
    }
    if (lower.includes("aujourd'hui") || lower.includes('hier')) {
      const result = new Date(baseDate);
      if (lower.includes('hier')) result.setDate(result.getDate() - 1);
      return fmt(result);
    }
  }

  // Case 3: fallback to loaded_at
  if (job.loaded_at) {
    const out = fmt(new Date(job.loaded_at));
    if (out) return out;
  }
  return raw || '—';
};

const JobCard = ({ job }) => {
  const navigate = useNavigate();
  const isFeatured = job.featured || job.source === 'Employeur Direct';
  const publicationDate = formatPublicationDate(job);

  return (
    <Card
      className={`job-card p-6 cursor-pointer border rounded-xl transition-all duration-200 ${
        isFeatured
          ? 'bg-gradient-to-br from-yellow-50 to-amber-50 border-yellow-200 hover:border-yellow-300 hover:shadow-lg hover:shadow-yellow-100'
          : 'bg-white border-slate-100 hover:border-sky-200 hover:shadow-md'
      }`}
      onClick={() => navigate(`/jobs/${job.id}`)}
      data-testid={`job-card-${job.id}`}
    >
      <div className="space-y-3">
        <div className="flex items-start justify-between gap-2">
          {/* Company logo */}
          <div className="shrink-0 h-12 w-12 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center overflow-hidden">
            <img
              src={job.logo || '/mosala-logo-square.jpg'}
              alt={job.entreprise}
              className="h-full w-full object-contain"
              onError={(e) => { e.target.src = '/mosala-logo-square.jpg'; }}
              data-testid={`job-logo-${job.id}`}
            />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <h3 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                {job.titre}
              </h3>
              {isFeatured && (
                <Badge className="bg-gradient-to-r from-yellow-400 to-amber-500 text-slate-900 text-xs font-semibold">
                  <Star className="h-3 w-3 mr-1" />
                  Premium
                </Badge>
              )}
            </div>
            <div className="flex items-center text-slate-600 text-sm mb-2">
              <Building2 className="h-4 w-4 mr-1" />
              <span className="truncate">{job.entreprise}</span>
            </div>
          </div>
          <Badge className="bg-sky-100 text-sky-700 hover:bg-sky-100 shrink-0">{job.domaine}</Badge>
        </div>

        <p className="text-slate-600 text-sm line-clamp-2">{job.description}</p>

        <div className="flex flex-wrap gap-3 text-sm text-slate-500">
          <div className="flex items-center">
            <MapPin className="h-4 w-4 mr-1" />
            <span>{job.localisation}</span>
          </div>
          <div className="flex items-center">
            <Briefcase className="h-4 w-4 mr-1" />
            <span>{job.type_contrat}</span>
          </div>
          {job.salaire && (
            <div className="flex items-center font-medium text-slate-700">
              <span>{job.salaire}</span>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between pt-2 border-t border-slate-100">
          <div className="flex items-center gap-3 text-xs text-slate-500">
            <span className="flex items-center" data-testid={`job-date-${job.id}`}>
              <Calendar className="h-3 w-3 mr-1" />
              {publicationDate}
            </span>
            {job.views > 0 && (
              <span className="flex items-center">
                <Eye className="h-3 w-3 mr-1" />
                {job.views} vues
              </span>
            )}
          </div>
          <Badge variant="outline" className="text-xs">{job.source}</Badge>
        </div>
      </div>
    </Card>
  );
};

export { formatPublicationDate };
export default JobCard;
